'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, useReducer, useState, useEffect, useMemo, Suspense, useRef, useCallback } from 'react';
import { getAllSaved, getAllGuestSaved, getAllVisited, subscribe as subscribeCacheUpdates } from '@/lib/client/interactionCache';
import { useExploreFilters as useFiltersHook } from '../hooks/useExploreFilters';
import { useExploreSearch as useSearchHook } from '../hooks/useExploreSearch';
import { useExploreMap as useMapHook } from '../hooks/useExploreMap';
import { useExploreLocation as useLocationHook } from '../hooks/useExploreLocation';
import { useExploreView as useViewHook } from '../hooks/useExploreView';
import { useExploreUrlParams } from '../hooks/useExploreUrlParams';
import { buildUrlParamKey, hasUrlFilters, resolveInitialExploreState } from '../utils/exploreState';
import { MARKER_TYPES, isHeartType } from '../constants';
const ExploreContext = createContext();

// Default map camera - used for initial state, not stored in reducer to avoid re-renders
const DEFAULT_MAP_CENTER = {
  lat: 39.0997,
  lng: -94.5786
}; // Kansas City
const DEFAULT_MAP_ZOOM = 10;

// KC Region boundaries - prevents navigation beyond this area
// Approximate bounds covering Kansas City metro area (~150 mile radius)
export const KC_REGION_BOUNDS = {
  north: 41.27,
  // Northern boundary
  south: 36.93,
  // Southern boundary
  east: -91.78,
  // Eastern boundary
  west: -97.38 // Western boundary
};

// Minimum zoom level (more zoomed out = smaller number)
export const MIN_ZOOM = 7;
export const SHOW_CLUSTERS = true; // Default: show clusters at medium zoom levels
export const FADE_ICONS = false;

// Helper function to check if coordinates are within KC region
export function isWithinKCRegion(lat, lng) {
  return lat >= KC_REGION_BOUNDS.south && lat <= KC_REGION_BOUNDS.north && lng >= KC_REGION_BOUNDS.west && lng <= KC_REGION_BOUNDS.east;
}
const initialState = {
  searchQuery: '',
  searchCommitted: false,
  // True when user has selected from dropdown (filters List/Photos views)
  viewMode: 'map',
  // 'map' | 'list'
  hasViewedMap: false,
  initialMarkers: [],
  markers: [],
  selectedItem: null,
  searchResults: [],
  // NOTE: mapCenter/mapZoom removed from state - managed locally in ExploreMap to prevent re-renders
  isLoading: false,
  error: null,
  filters: {
    hearts: true,
    // Default: ON (show all hearts)
    communities: {
      all: true,
      // Default: ON (show all communities)
      selected: []
    },
    artists: true,
    // Default: ON (show all artists)
    attractions: {
      all: true,
      // Default: ON (show all attractions)
      selected: []
    },
    events: true,
    // Default: ON (show all events)
    promotions: {
      all: true,
      // Default: ON (show all promotions)
      dining: true,
      attractions: true,
      sports: true,
      arts: true
    }
  },
  userLocation: null,
  locationPermission: 'prompt',
  // 'granted' | 'denied' | 'prompt' | 'dismissed'
  showClusters: SHOW_CLUSTERS,
  // Default: show clusters at medium zoom levels
  fadeIcons: FADE_ICONS,
  // Default: do not fade icons at medium zoom levels
  heartsScope: 'all',
  // all | saved | visited
  sortMode: 'alphabetical',
  // alphabetical | distance
  sortModeTouched: false
};
function applyHeartsScope(items, scope) {
  if (!Array.isArray(items) || items.length === 0 || scope === 'all') return items;
  if (scope === 'saved') {
    // Keep non-heart markers; only show saved heart variant (including saved+visited)
    return items.filter(item => !isHeartType(item.type) || item.type === MARKER_TYPES.SAVED_HEART || item.type === MARKER_TYPES.SAVED_VISITED_HEART);
  }
  if (scope === 'visited') {
    // Keep non-heart markers; only show visited heart variant (including saved+visited)
    return items.filter(item => !isHeartType(item.type) || item.type === MARKER_TYPES.VISITED_HEART || item.type === MARKER_TYPES.SAVED_VISITED_HEART);
  }
  return items;
}
function exploreReducer(state, action) {
  switch (action.type) {
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.payload
      };
    case 'SET_SEARCH_COMMITTED':
      return {
        ...state,
        searchCommitted: action.payload
      };
    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewMode: action.payload,
        hasViewedMap: state.hasViewedMap || action.payload === 'map'
      };
    case 'SET_MARKERS':
      return {
        ...state,
        markers: action.payload
      };
    case 'UPDATE_MARKER':
      return {
        ...state,
        markers: action.payload
      };
    case 'UPDATE_MARKER_TYPE':
      return {
        ...state,
        markers: state.markers.map(m => m.id === action.payload.id ? {
          ...m,
          type: action.payload.type
        } : m)
      };
    case 'SET_SELECTED_ITEM':
      return {
        ...state,
        selectedItem: action.payload
      };
    case 'SET_SEARCH_RESULTS':
      return {
        ...state,
        searchResults: action.payload
      };
    // MAP_CENTER and MAP_ZOOM actions removed - camera state is now local to ExploreMap
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };
    case 'RESET_SEARCH':
      return {
        ...state,
        searchQuery: '',
        searchCommitted: false,
        searchResults: [],
        selectedItem: null
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        }
      };
    case 'SET_USER_LOCATION':
      return {
        ...state,
        userLocation: action.payload
      };
    case 'SET_LOCATION_PERMISSION':
      return {
        ...state,
        locationPermission: action.payload
      };
    case 'SET_CLUSTERING':
      return {
        ...state,
        showClusters: action.payload
      };
    case 'SET_FADE_ICONS':
      return {
        ...state,
        fadeIcons: action.payload
      };
    case 'SET_HEARTS_SCOPE':
      return {
        ...state,
        heartsScope: action.payload
      };
    case 'SET_SORT_MODE':
      return {
        ...state,
        sortMode: action.payload.mode,
        sortModeTouched: action.payload.touched ?? state.sortModeTouched
      };
    default:
      return state;
  }
}

// Deterministic initializer so SSR and initial client render stay in sync
function initializeState({
  initialMarkers,
  initialUrlParams,
  attractionCategories
}) {
  const resolved = resolveInitialExploreState(initialMarkers, initialUrlParams, attractionCategories);
  return {
    ...initialState,
    initialMarkers,
    viewMode: resolved.viewMode,
    filters: resolved.filters,
    searchQuery: resolved.searchQuery,
    hasViewedMap: resolved.viewMode === 'map'
  };
}
const FILTER_STORAGE_KEY = 'explore_filters';
function toNumericId(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}
function getHeartCandidateIds(item) {
  const ids = [toNumericId(item?.id), toNumericId(item?.baseId)].filter(id => id !== null);
  return ids;
}
function getSanitizedInteractionSets() {
  const savedIds = [...getAllSaved(), ...getAllGuestSaved()].map(toNumericId).filter(id => id !== null);
  const visitedIds = getAllVisited().map(visit => toNumericId(visit?.heartId)).filter(id => id !== null);
  return {
    savedIds: new Set(savedIds),
    visitedIds: new Set(visitedIds)
  };
}
function loadFiltersFromSession() {
  if (typeof window === 'undefined') return null;
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('Failed to load filters from sessionStorage:', error);
    return null;
  }
}
export function ExploreProvider({
  children,
  initialItems = [],
  initialPromotionMarkers = [],
  initialPromotionListItems = [],
  industriesData = null,
  attractionCategories = null,
  initialUrlParams = {}
}) {
  const initialMarkers = useMemo(() => [...initialItems, ...initialPromotionMarkers], [initialItems, initialPromotionMarkers]);
  const initialListItems = useMemo(() => [...initialItems, ...initialPromotionListItems], [initialItems, initialPromotionListItems]);

  // Lazy initialization keeps SSR <> client markup identical
  const [state, dispatch] = useReducer(exploreReducer, {
    initialMarkers,
    initialUrlParams,
    attractionCategories
  }, initializeState);
  const [isHydrated, setIsHydrated] = useState(false);

  // Track client-side saved/visited IDs from interactionCache for accurate scope filtering
  const [cacheInteractions, setCacheInteractions] = useState(() => {
    if (typeof window === 'undefined') return {
      savedIds: new Set(),
      visitedIds: new Set()
    };
    return getSanitizedInteractionSets();
  });
  const initialUrlParamsKey = buildUrlParamKey(initialUrlParams);
  const suppressUrlSyncRef = useRef(false);

  // Derive marker/list item types from live interaction cache (visited > saved > heart).
  const effectiveInitialMarkers = useMemo(() => {
    if (cacheInteractions.savedIds.size === 0 && cacheInteractions.visitedIds.size === 0) {
      return initialMarkers;
    }
    return initialMarkers.map(item => {
      if (!isHeartType(item.type)) return item;
      const candidateIds = getHeartCandidateIds(item);
      const isSaved = candidateIds.some(id => cacheInteractions.savedIds.has(id));
      const isVisited = candidateIds.some(id => cacheInteractions.visitedIds.has(id));
      if (isSaved && isVisited) {
        return {
          ...item,
          type: MARKER_TYPES.SAVED_VISITED_HEART
        };
      }
      if (isVisited) {
        return {
          ...item,
          type: MARKER_TYPES.VISITED_HEART
        };
      }
      if (isSaved) {
        return {
          ...item,
          type: MARKER_TYPES.SAVED_HEART
        };
      }
      return item.type !== MARKER_TYPES.HEART ? {
        ...item,
        type: MARKER_TYPES.HEART
      } : item;
    });
  }, [initialMarkers, cacheInteractions]);
  const effectiveInitialListItems = useMemo(() => {
    if (cacheInteractions.savedIds.size === 0 && cacheInteractions.visitedIds.size === 0) {
      return initialListItems;
    }
    return initialListItems.map(item => {
      if (!isHeartType(item.type)) return item;
      const candidateIds = getHeartCandidateIds(item);
      const isSaved = candidateIds.some(id => cacheInteractions.savedIds.has(id));
      const isVisited = candidateIds.some(id => cacheInteractions.visitedIds.has(id));
      if (isSaved && isVisited) {
        return {
          ...item,
          type: MARKER_TYPES.SAVED_VISITED_HEART
        };
      }
      if (isVisited) {
        return {
          ...item,
          type: MARKER_TYPES.VISITED_HEART
        };
      }
      if (isSaved) {
        return {
          ...item,
          type: MARKER_TYPES.SAVED_HEART
        };
      }
      return item.type !== MARKER_TYPES.HEART ? {
        ...item,
        type: MARKER_TYPES.HEART
      } : item;
    });
  }, [initialListItems, cacheInteractions]);

  // Initialize hooks for each concern
  // Ref for imperative map camera control - avoids storing camera in state
  const mapCameraRef = useRef({
    center: DEFAULT_MAP_CENTER,
    zoom: DEFAULT_MAP_ZOOM,
    setCamera: null // Will be set by ExploreMap
  });

  // Imperative camera setters that don't trigger re-renders
  const setMapCenter = useCallback(center => {
    mapCameraRef.current.center = center;
    mapCameraRef.current.setCamera?.({
      center
    });
  }, []);
  const setMapZoom = useCallback(zoom => {
    mapCameraRef.current.zoom = zoom;
    mapCameraRef.current.setCamera?.({
      zoom
    });
  }, []);

  // Callback for when permission changes (e.g., user re-enables in browser settings)
  const handlePermissionChange = useCallback(() => {
    // Clear sessionStorage flag so auto-request can fire again
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('locationRequested');
    }
  }, []);
  const filterHook = useFiltersHook(state, dispatch, initialMarkers, {
    isHydrated,
    suppressUrlSyncRef,
    attractionCategories
  });
  const searchHook = useSearchHook(state, dispatch, effectiveInitialMarkers, effectiveInitialListItems, filterHook.filterMarkers);
  const mapHook = useMapHook(state, dispatch);
  // Pass imperative setters to location hook instead of dispatch
  const locationHook = useLocationHook(state, dispatch, {
    setMapCenter,
    setMapZoom,
    onPermissionChange: handlePermissionChange,
    isHydrated
  });
  // Pass buildUrlParamsFromFilters to viewHook so it can preserve filter state when changing views
  const viewHook = useViewHook(state, dispatch, filterHook.buildUrlParamsFromFilters, initialMarkers);
  const setHeartsScope = useCallback(scope => {
    dispatch({
      type: 'SET_HEARTS_SCOPE',
      payload: scope
    });
    dispatch({
      type: 'SET_SELECTED_ITEM',
      payload: null
    });
    mapCameraRef.current.setCamera?.({
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM
    });
  }, [dispatch]);
  const requestSortMode = useCallback(async mode => {
    if (mode !== 'distance') {
      dispatch({
        type: 'SET_SORT_MODE',
        payload: {
          mode,
          touched: true
        }
      });
      return true;
    }
    if (state.locationPermission === 'granted' && state.userLocation) {
      dispatch({
        type: 'SET_SORT_MODE',
        payload: {
          mode: 'distance',
          touched: true
        }
      });
      return true;
    }
    const result = await locationHook.requestLocation();
    if (result?.success) {
      dispatch({
        type: 'SET_SORT_MODE',
        payload: {
          mode: 'distance',
          touched: true
        }
      });
      return true;
    }
    dispatch({
      type: 'SET_SORT_MODE',
      payload: {
        mode: 'alphabetical',
        touched: true
      }
    });
    return false;
  }, [locationHook, state.locationPermission, state.userLocation]);
  useEffect(() => {
    if (state.sortModeTouched) return;
    if (state.locationPermission === 'granted' && state.sortMode !== 'distance') {
      dispatch({
        type: 'SET_SORT_MODE',
        payload: {
          mode: 'distance',
          touched: false
        }
      });
      return;
    }
    if (state.locationPermission !== 'granted' && state.sortMode !== 'alphabetical') {
      dispatch({
        type: 'SET_SORT_MODE',
        payload: {
          mode: 'alphabetical',
          touched: false
        }
      });
    }
  }, [state.locationPermission, state.sortMode, state.sortModeTouched]);

  // NOTE: URL params sync is handled by UrlParamsSync component below (wrapped in Suspense)
  // Do NOT call useExploreUrlParams here to avoid duplicate calls

  // Apply session filters if URL has no filters, then mark hydrated
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    if (!hasUrlFilters(initialUrlParams)) {
      const sessionFilters = loadFiltersFromSession();
      if (sessionFilters) {
        dispatch({
          type: 'SET_FILTERS',
          payload: sessionFilters
        });
      }
    }
    setIsHydrated(true);
  }, [dispatch, initialUrlParams]);

  // Auto-disable clustering when only hearts are active
  useEffect(() => {
    // Determine if only hearts filter is active
    const onlyHeartsActive = state.filters.hearts && !state.filters.artists && !state.filters.events && !state.filters.attractions.all && !state.filters.promotions.dining && !state.filters.promotions.attractions && !state.filters.promotions.sports && !state.filters.promotions.arts;

    // Set clustering based on whether only hearts are active
    const shouldShowClusters = !onlyHeartsActive;
    if (state.showClusters !== shouldShowClusters) {
      dispatch({
        type: 'SET_CLUSTERING',
        payload: shouldShowClusters
      });
    }
  }, [state.filters, state.showClusters]);

  // Apply filters whenever they change or when initial markers are loaded
  // Use ref to track previous markers to avoid unnecessary dispatches
  const prevMarkersRef = useRef(null);

  // Sync interactionCache into state so marker types reflect real-time saves/visits
  useEffect(() => {
    const update = () => setCacheInteractions(getSanitizedInteractionSets());
    update();
    return subscribeCacheUpdates(event => {
      if (['save', 'unsave', 'visit', 'visit_remove', 'init', 'clear', 'sync', 'guest_save', 'guest_unsave', 'guest_sync'].includes(event.type)) {
        update();
      }
    });
  }, []);

  // Fallback hydrate from server on refresh when local interaction cache is empty/stale.
  useEffect(() => {
    let cancelled = false;
    const hydrateFromServer = async () => {
      try {
        const response = await fetch('/api/me/interactions', {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin'
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        const fetchedSaved = Array.isArray(data?.savedHearts) ? data.savedHearts.map(toNumericId).filter(id => id !== null) : [];
        const fetchedVisited = Array.isArray(data?.visitedHearts) ? data.visitedHearts.map(visit => toNumericId(visit?.heartId ?? visit)).filter(id => id !== null) : [];
        if (fetchedSaved.length === 0 && fetchedVisited.length === 0) return;
        setCacheInteractions(prev => ({
          savedIds: new Set([...prev.savedIds, ...fetchedSaved]),
          visitedIds: new Set([...prev.visitedIds, ...fetchedVisited])
        }));
      } catch (error) {
        console.warn('Failed to hydrate explore interactions from server:', error);
      }
    };
    hydrateFromServer();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (effectiveInitialMarkers.length === 0) return;

    // Pass viewMode to enable view-specific filtering (e.g., artists in list vs map)
    const baseFilteredMarkers = filterHook.filterMarkers(effectiveInitialMarkers, state.filters, state.viewMode);
    // Filter markers to only show the matching heart variant for the selected scope
    const filteredMarkers = applyHeartsScope(baseFilteredMarkers, state.heartsScope);

    // Only dispatch if markers actually changed (shallow compare by length and first/last id)
    const prevMarkers = prevMarkersRef.current;
    const markersChanged = !prevMarkers || prevMarkers.length !== filteredMarkers.length || prevMarkers[0]?.id !== filteredMarkers[0]?.id || prevMarkers[prevMarkers.length - 1]?.id !== filteredMarkers[filteredMarkers.length - 1]?.id;
    if (markersChanged) {
      prevMarkersRef.current = filteredMarkers;
      dispatch({
        type: 'SET_MARKERS',
        payload: filteredMarkers
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveInitialMarkers, state.filters, state.viewMode, state.heartsScope]);
  const listItems = useMemo(() => {
    const filtered = filterHook.filterMarkers(effectiveInitialListItems, state.filters, 'list');
    return applyHeartsScope(filtered, state.heartsScope);
  }, [effectiveInitialListItems, state.filters, state.heartsScope]);
  const heartsProgress = useMemo(() => {
    const allHearts = effectiveInitialMarkers.filter(item => isHeartType(item.type));
    const total = allHearts.length;
    const visited = allHearts.filter(item => item.type === MARKER_TYPES.VISITED_HEART || item.type === MARKER_TYPES.SAVED_VISITED_HEART).length;
    return {
      visited,
      total
    };
  }, [effectiveInitialMarkers]);
  const updateMarkerType = useCallback((id, type) => {
    dispatch({
      type: 'UPDATE_MARKER_TYPE',
      payload: {
        id,
        type
      }
    });
  }, [dispatch]);

  // Combine all hooks into context value
  const value = {
    ...state,
    // Industries data (server-fetched)
    industriesData,
    // Attraction categories (server-fetched)
    attractionCategories: filterHook.attractionCategories,
    // Filter hooks
    filters: filterHook.filters,
    setFilters: filterHook.setFilters,
    filterMarkers: filterHook.filterMarkers,
    getUniqueCommunities: filterHook.getUniqueCommunities,
    getUniqueAttractionCategories: filterHook.getUniqueAttractionCategories,
    getActiveFilterCount: filterHook.getActiveFilterCount,
    // Search hooks
    searchQuery: searchHook.searchQuery,
    searchCommitted: state.searchCommitted,
    searchResults: searchHook.searchResults,
    isLoading: searchHook.isLoading,
    error: searchHook.error,
    setSearchQuery: searchHook.setSearchQuery,
    search: searchHook.search,
    clearSearch: searchHook.clearSearch,
    commitSearch: searchHook.commitSearch,
    // Map hooks - camera state is ref-based to avoid re-renders
    mapCameraRef,
    defaultMapCenter: DEFAULT_MAP_CENTER,
    defaultMapZoom: DEFAULT_MAP_ZOOM,
    markers: mapHook.markers,
    listItems,
    selectedItem: mapHook.selectedItem,
    setMapCenter,
    setMapZoom,
    setSelectedItem: mapHook.setSelectedItem,
    updateMarkerType,
    // Location hooks
    userLocation: locationHook.userLocation,
    locationPermission: locationHook.locationPermission,
    setUserLocation: locationHook.setUserLocation,
    setLocationPermission: locationHook.setLocationPermission,
    requestUserLocation: locationHook.requestLocation,
    centerOnUserLocation: locationHook.centerOnUserLocation,
    disableLocation: locationHook.disableLocation,
    clearLocationDismissal: locationHook.clearDismissal,
    // View hooks
    viewMode: viewHook.viewMode,
    setViewMode: viewHook.setViewMode,
    hasViewedMap: state.hasViewedMap,
    isHydrated,
    // Raw data for search autofill
    initialMarkers,
    initialListItems,
    showClusters: state.showClusters,
    fadeIcons: state.fadeIcons,
    heartsScope: state.heartsScope,
    setHeartsScope,
    sortMode: state.sortMode,
    requestSortMode,
    heartsProgress
  };
  return <ExploreContext.Provider value={value}>
      {/* Wrap URL params sync in Suspense for static generation compatibility */}
      <Suspense fallback={null}>
        <UrlParamsSync dispatch={dispatch} setFilters={filterHook.setFilters} setViewMode={viewHook.setViewMode} initialMarkers={initialMarkers} attractionCategories={attractionCategories} isHydrated={isHydrated} initialUrlParamsKey={initialUrlParamsKey} suppressUrlSyncRef={suppressUrlSyncRef} />
      </Suspense>
      {children}
    </ExploreContext.Provider>;
}

/**
 * Component to sync URL params with explore state.
 * Wrapped in Suspense because useSearchParams requires it for static generation.
 */
function UrlParamsSync(t0) {
  const $ = _c(4);
  const {
    dispatch,
    setFilters,
    setViewMode,
    initialMarkers,
    attractionCategories,
    isHydrated,
    initialUrlParamsKey,
    suppressUrlSyncRef
  } = t0;
  let t1;
  if ($[0] !== initialUrlParamsKey || $[1] !== isHydrated || $[2] !== suppressUrlSyncRef) {
    t1 = {
      isHydrated,
      initialUrlParamsKey,
      suppressUrlSyncRef
    };
    $[0] = initialUrlParamsKey;
    $[1] = isHydrated;
    $[2] = suppressUrlSyncRef;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useExploreUrlParams(dispatch, setFilters, setViewMode, initialMarkers, attractionCategories, t1);
  return null;
}
export function useExplore() {
  const context = useContext(ExploreContext);
  if (!context) {
    throw new Error("useExplore must be used within an ExploreProvider");
  }
  return context;
}